/**
 * Section configuration
 *
 * Sections are defined by their backend `category` values.
 * questionIds are derived dynamically from API data via buildSections().
 *
 * When new questions are seeded in the backend with a category,
 * they automatically appear in the correct section — no frontend
 * config change needed.
 */

/**
 * Section definitions — maps display metadata to backend categories.
 * `categories` matches the Question model's `category` field in the backend.
 */
export const SECTION_DEFS = [
  {
    id: 'section0',
    title: 'Section 0: Welcome to A Whole Family Matter',
    description: 'Your entry point — a single question that frames how you want to engage with the platform, who walks alongside you, and what would help you begin.',
    categories: ['Welcome'],
  },
  {
    id: 'section1',
    title: 'Section 1: Life Story Work',
    description: 'These questions help you share who you are — your story, strengths, and what shaped you — so your care team can truly know the person behind the patient.',
    categories: ['Life Story Work'],
  },
  {
    id: 'section2',
    title: 'Section 2: What Matters Most',
    description: 'These questions explore what matters most to you — your relationships, values, beliefs, and what gives your life meaning.',
    categories: ['What Matters Most'],
  },
  {
    id: 'section3',
    title: 'Section 3: Advance Care Planning (Part 1)',
    description: 'These questions help you explore your values and preferences for medical care decisions.',
    categories: ['UHCDA Priority Values', 'UHCDA Treatment Decisions'],
  },
  {
    id: 'section4',
    title: 'Section 4: Advance Care Planning (Part 2) Agent Authority',
    description: 'These questions help you define the authority and decision-making powers you want to give your healthcare agent.',
    categories: ['Advance Care Planning - Agent Authority'],
  },
  {
    id: 'section5',
    title: 'Section 5: End of Life Planning',
    description: 'These questions explore your preferences for where you want to die, how you feel about hospice and palliative care, voluntary stopping eating and drinking, organ donation, body disposition, and your digital legacy.',
    categories: ['End of Life Planning'],
  },
  {
    id: 'section6',
    title: 'Section 6: End of Life Wishes',
    description: 'These questions explore what makes you feel valued, what brings you comfort and joy, and the meaningful experiences you still want to share with loved ones.',
    categories: ['End of Life Wishes'],
  },
  {
    id: 'section7',
    title: 'Section 7: Would You Recommend A Whole Family Matter',
    description: 'Your exit point — your verdict on the platform itself: whether you would recommend it, what got in the way, and what would change your mind.',
    categories: ['Recommendation'],
  },
];

/**
 * Build full sections array with questionIds derived from API questions data.
 * @param {Array} questions - Questions from questionsService.getAll()
 * @returns {Array} Sections with computed questionIds
 */
export const buildSections = (questions = []) => {
  return SECTION_DEFS.map((def) => ({
    ...def,
    questionIds: questions
      .filter((q) => def.categories.includes(q.category))
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map((q) => q.id),
  }));
};

/**
 * Build a question-to-section lookup map from API questions data.
 * @param {Array} questions - Questions from questionsService.getAll()
 * @returns {Object} Map of lowercase questionId -> section object
 */
export const buildSectionByQuestion = (questions = []) => {
  const sections = buildSections(questions);
  const map = {};
  for (const section of sections) {
    for (const qId of section.questionIds) {
      map[qId.toLowerCase()] = section;
    }
  }
  return map;
};

// ---------------------------------------------------------------------------
// Backward-compatible exports (used by QuestionnaireFlow which doesn't have
// the full questions array at import time). These operate on whatever SECTIONS
// are currently hydrated.
// ---------------------------------------------------------------------------

/** Mutable sections array — hydrated by the first component that fetches questions. */
export let SECTIONS = SECTION_DEFS.map((def) => ({ ...def, questionIds: [] }));

/**
 * Hydrate SECTIONS with live question data. Call once after fetching questions.
 * All downstream consumers that import SECTIONS will see updated questionIds.
 */
export const hydrateSections = (questions = []) => {
  const built = buildSections(questions);
  // Mutate in place so existing imports see the change
  SECTIONS.length = 0;
  SECTIONS.push(...built);
  return SECTIONS;
};

/**
 * Find which section a question belongs to.
 */
export const getSectionForQuestion = (questionId) => {
  return SECTIONS.find((s) => s.questionIds.includes(questionId)) || null;
};

/**
 * Get all question IDs for a given section.
 */
export const getQuestionIdsForSection = (sectionId) => {
  const section = SECTIONS.find((s) => s.id === sectionId);
  return section ? section.questionIds : [];
};

/**
 * Check if a question is the last question of the last section.
 */
export const isLastQuestionOfLastSection = (questionId) => {
  const lastSection = SECTIONS[SECTIONS.length - 1];
  if (!lastSection) return false;
  return lastSection.questionIds[lastSection.questionIds.length - 1] === questionId;
};

/**
 * Get the next question ID in the same section.
 */
export const getNextQuestionId = (questionId) => {
  const section = getSectionForQuestion(questionId);
  if (!section) return null;
  const index = section.questionIds.indexOf(questionId);
  if (index === -1 || index === section.questionIds.length - 1) return null;
  return section.questionIds[index + 1];
};
